import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import cn from 'classnames';

import { useTheme } from '@websky/core/src/theme';
import CheckoutAdditionalService from '../CheckoutAdditionalService';
import Modal from '@websky/core/src/Modal';
import VipServiceSelect from '@websky/core/src/VipServiceSelect';
import { getSegments } from '@websky/core/src/Checkout/store/order/selectors';
import { MOBILE_MIN_WIDTH } from '@websky/core/src/utils';
import { SlideBottom } from '@websky/core/src/SlideBottom/components/SlideBottom';
import { useOrderService } from '@websky/core/src/CheckoutAdditionalService/components/hooks';
import {
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	OrderAdditionalServiceGdsServiceServiceType
} from '@websky/graphql';
import { AdditionalServiceType, ServiceButton } from '@websky/core/src/CheckoutAdditionalService/types';
import ServiceError from '@websky/core/src/CheckoutAdditionalService/components/ServiceError/ServiceError';
import { Money } from '@websky/core/src/Money/types';
import { SafeHtml } from '@websky/core/src/__fsd__/shared/ui';

interface Props {
	className?: string;
	header: string;
	service: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;
	price?: Money;
}

const BusinessService: React.FC<Props> = ({ className, header, service, price }) => {
	const theme = useTheme('CheckoutAdditionalService').Business;

	const segments = useSelector(getSegments);

	const {
		isSelected,
		selectedServices,
		unconfirmedServices,
		renderSubheader,
		onOpen,
		onClose,
		setOpened,
		onSaveHandler,
		onRemoveHandler,
		passengers,
		opened
	} = useOrderService([service], OrderAdditionalServiceGdsServiceServiceType.BusinessLounge);

	const { t } = useTranslation('Checkout');

	const buttons: ServiceButton[] = useMemo(() => {
		if (unconfirmedServices.length) {
			return [
				{
					className: theme.button__remove,
					action: () => onRemoveHandler(unconfirmedServices),
					text: t('Remove'),
					buttonType: 'change',
					variant: 'outline'
				}
			];
		}

		return [
			{
				action: () => setOpened(true),
				text: t('Change'),
				buttonType: 'change'
			}
		];
	}, [unconfirmedServices]);

	return (
		<>
			<CheckoutAdditionalService
				header={header}
				description={service.description}
				className={cn(theme.business, className)}
				priceFrom={price}
				priceTotal={price}
				isSelected={isSelected}
				addClick={onOpen}
				descriptionClassName={theme.descriptions}
				buttons={buttons}
				serviceType={AdditionalServiceType.BusinessLounge}
			>
				<ServiceError isShow={!!unconfirmedServices.length}>
					<SafeHtml tag="div" html={service.description} />
				</ServiceError>
			</CheckoutAdditionalService>

			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				<Modal classes={{ paper: theme.paper }} open={opened} onClose={onClose} maxWidth="sm">
					<VipServiceSelect
						className={theme.serviceSelect}
						headerClassName={theme.header}
						header={header}
						passengers={passengers}
						services={[service]}
						segments={segments}
						onSave={onSaveHandler}
						selectedServices={selectedServices}
						onClose={onClose}
						subheader={renderSubheader()}
					/>
				</Modal>
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				<SlideBottom isOpen={opened} onClose={onClose}>
					<VipServiceSelect
						className={theme.serviceSelect}
						headerClassName={theme.header}
						header={header}
						passengers={passengers}
						services={[service]}
						segments={segments}
						onSave={onSaveHandler}
						selectedServices={selectedServices}
						onClose={onClose}
						subheader={renderSubheader()}
					/>
				</SlideBottom>
			</MediaQuery>
		</>
	);
};

export default BusinessService;
